import { cn } from "@babybjorn/ui";
import type { FC } from "react";
import Link from "../../../components/general/Link";
import Button from "../Button";
import Counter from "../Counter";
import ImageContainer from "../ImageContainers";
import * as s from "./ModalCartItem.module.scss";
import ConditionalWrapper from "../../ConditionalWrapper";

export type ModalCartItemProps = {
	productName: string;
	productImage: { src: string; alt?: string };
	productLink?: string;
	price: string;
	quantity?: number;
	quantityLabel?: string;
	disabled?: boolean;
	variantLabel?: string;
	increment?: () => Promise<void>;
	decrement?: () => Promise<void>;
	incrementLabel?: string;
	decrementLabel?: string;
	remove?: () => void;
	overview?: boolean;
	checkout?: boolean;
	search?: boolean;
	enableQuantityCounter?: boolean;
	enableCloseButton?: boolean;
	productKey?: string;
};

const ModalCartItem: FC<ModalCartItemProps> = ({
	productName,
	productImage,
	productLink,
	price,
	quantity,
	quantityLabel,
	variantLabel,
	increment,
	decrement,
	incrementLabel,
	decrementLabel,
	remove,
	disabled = false,
	overview = false,
	checkout = false, //Used for Checkout
	search = false, //Used for Search
	enableQuantityCounter = true,
	enableCloseButton = true,
}) => {
	return (
		<li
			className={cn(s.itemWrapper, {
				[s.overview]: overview || search,
			})}
		>
			<section className={s.infoContainer}>
				<div className={s.infoContent}>
					<p className={s.checkout}>
						<ConditionalWrapper
							shouldWrap={productLink !== undefined}
							wrapper={(children) => (
								<Link
									href={productLink}
									target="_self"
									className={s.productLink}
								>
									{children}
								</Link>
							)}
						>
							<p>{productName}</p>
						</ConditionalWrapper>
					</p>
					{overview && quantity && quantityLabel && (
						<span className={s.checkout}>
							{quantityLabel}: {quantity}
						</span>
					)}
					<span className={s.checkout}>{variantLabel}</span>
				</div>

				<div
					className={cn(s.priceContainer, {
						[s.disabled]: disabled,
						[s.overview]: overview,
					})}
				>
					{quantity &&
						increment &&
						decrement &&
						!overview &&
						enableQuantityCounter && (
							<Counter
								count={quantity}
								decrement={decrement}
								increment={increment}
								incrementLabel={incrementLabel ?? "increment"}
								decrementLabel={decrementLabel ?? "decrement"}
							/>
						)}
					<h5 className={cn({ [s.checkout]: checkout || search })}>{price}</h5>
				</div>
			</section>
			<div className={s.imageContainer}>
				<ConditionalWrapper
					shouldWrap={productLink !== undefined}
					wrapper={(children) => (
						<Link href={productLink} target="_self">
							{children}
						</Link>
					)}
				>
					<ImageContainer
						width={100}
						sizes="100px"
						alt={productImage.alt ?? ""}
						ratio="portrait"
						objectFit="cover"
						imgSrc={productImage.src}
						className={cn(s.image, {
							[s.checkout]: checkout,
							[s.search]: search,
						})}
					/>
				</ConditionalWrapper>
				{remove && enableCloseButton && (
					<Button
						icon="close"
						buttonType="tiny"
						variant="secondary"
						iconSize={16}
						className={s.closeButton}
						onClick={remove}
					/>
				)}
			</div>
		</li>
	);
};

export default ModalCartItem;
